import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  tasks: [],
  comments: {}, // Comments grouped by TaskID: { [taskId]: [comments] }
  loading: false,
  error: null,
  filters: {
    assignedTo: null,
    priority: null,
    status: null,
    showCompleted: false,
  },
  sortBy: "DueDate", // DueDate, Priority, DateCreated
  sortOrder: "asc", // asc, desc
};

const engineeringTasksSlice = createSlice({
  name: "engineeringTasks",
  initialState,
  reducers: {
    replaceAllTaskComments: (state, action) => {
      const grouped = {};
      for (const comment of action.payload) {
        const taskId = comment.TaskID ?? comment.taskID ?? comment.taskId;
        if (taskId == null) continue;
        (grouped[taskId] ||= []).push(comment);
      }
      state.comments = grouped;
    },
    setTasks: (state, action) => {
      state.tasks = action.payload;
      state.loading = false;
      state.error = null;
    },
    addTask: (state, action) => {
      state.tasks.push(action.payload);
    },
    updateTask: (state, action) => {
      const index = state.tasks.findIndex(task => task.ID === action.payload.ID);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
    },
    deleteTask: (state, action) => {
      state.tasks = state.tasks.filter(task => task.ID !== action.payload);
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    setFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    toggleSortOrder: (state) => {
      state.sortOrder = state.sortOrder === "asc" ? "desc" : "asc";
    },
    clearTasks: (state) => {
      state.tasks = [];
      state.error = null;
    },
    setTaskComments: (state, action) => {
      const { taskId, comments } = action.payload;
      // Ensure comments object exists
      if (!state.comments) {
        state.comments = {};
      }
      state.comments[taskId] = comments;
    },
    addTaskComment: (state, action) => {
      const comment = action.payload;
      const taskId = comment.TaskID;
      // Ensure comments object exists
      if (!state.comments) {
        state.comments = {};
      }
      if (!state.comments[taskId]) {
        state.comments[taskId] = [];
      }
      state.comments[taskId].push(comment);
    },
    clearTaskComments: (state, action) => {
      const taskId = action.payload;
      if (state.comments && state.comments[taskId]) {
        delete state.comments[taskId];
      }
    },
  },
});

export const {
  replaceAllTaskComments,
  setTasks,
  addTask,
  updateTask,
  deleteTask,
  setLoading,
  setError,
  setFilter,
  resetFilters,
  setSortBy,
  setSortOrder,
  toggleSortOrder,
  clearTasks,
  setTaskComments,
  addTaskComment,
  clearTaskComments,
} = engineeringTasksSlice.actions;

export default engineeringTasksSlice.reducer;
